import React, { useState, useEffect } from "react";
import { completeProfile } from "../../api/registrationApi";
import { useNavigate } from "react-router-dom";
import "./AlumniDetails.css";

function AlumniDetails() {
  const navigate = useNavigate();
  const storedUserDetails = JSON.parse(localStorage.getItem("userDetails")) || {};

  const [formData, setFormData] = useState({
    firstName: storedUserDetails.firstName || "",
    lastName: storedUserDetails.lastName || "",
    enrollmentNumber: storedUserDetails.enrollmentNumber || "",
    role: storedUserDetails.role || "",
    year: storedUserDetails.year || "",
    photo: null,
    workEmail: "",
    password: "",
    confirmPassword: "",
    currentCompany: "",
    jobTitle: "",
    workExperience: "",
    college: "",
    degree: "",
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (formData.photo) {
      const objectUrl = URL.createObjectURL(formData.photo);
      setPhotoPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl); // Cleanup to prevent memory leaks
    } else {
      setPhotoPreview(null);
    }
  }, [formData.photo]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const handleCancel = () => {
    setFormData((prevData) => ({ ...prevData, photo: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.photo) {
        alert("Please upload a profile photo!");
        setLoading(false);
        return;
    }

    if (formData.password !== formData.confirmPassword) {
        alert("Passwords do not match!");
        setLoading(false);
        return;
    }

    const data = new FormData();

    // ✅ Append all text fields
    Object.keys(formData).forEach((key) => {
        if (key !== "photo" && formData[key]) {
            data.append(key, formData[key]);
        }
    });

    // ✅ Append photo correctly
    if (formData.photo instanceof File) {
        data.append("photo", formData.photo);
    }

    console.log("📤 [DEBUG] FormData before submission:");
    for (let [key, value] of data.entries()) {
        console.log(`${key}:`, key === "photo" ? "Image Uploaded" : value);
    }

    try {
        const response = await completeProfile(data);

        if (!response || response.error) {
            throw new Error(response.error || "Profile submission failed");
        }

        alert("✅ Profile submitted successfully!");
        navigate("/", { replace: true });

    } catch (error) {
        console.error("❌ Error submitting profile:", error);
        alert(`Failed to submit profile. Reason: ${error.message}`);
    } finally {
        setLoading(false);
    }
};

  return (
    <div className="alumni-details">
      <h1>Complete Your Alumni Profile</h1>
      <form onSubmit={handleSubmit}>

        {/* Profile Photo Upload */}
        <div className="form-group">
          <div className="photo-placeholder">
            {photoPreview ? (
              <img src={photoPreview} alt="Profile Preview" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="gray" width="50%" height="50%">
                <circle cx="12" cy="8" r="4" />
                <path d="M2 20c0-5.33 4.33-8 10-8s10 2.67 10 8H2z" />
              </svg>
            )}
          </div>
          <div className="photo-options">
            <label htmlFor="photo-upload" className="upload-btn">
              Upload Photo
            </label>
            <input type="file" id="photo-upload" name="photo" accept="image/*" onChange={handleChange} style={{ display: "none" }} />
            <button type="button" className="cancel-btn" onClick={handleCancel} disabled={!formData.photo}>
              Remove
            </button>
          </div>
        </div>

        {/* Auto-filled Fields */}
        {["firstName", "lastName", "enrollmentNumber"].map((field) => (
          <div className="form-group" key={field}>
            <label>{field.replace(/([A-Z])/g, " $1")}:</label>
            <input type="text" value={formData[field]} disabled />
          </div>
        ))}

        {/* Work Email */}
        <div className="form-group">
          <label htmlFor="workEmail">Work Email:</label>
          <input type="email" id="workEmail" name="workEmail" placeholder="Enter Work Email" value={formData.workEmail} onChange={handleChange} required />
        </div>

        {/* Password Fields */}
        {["password", "confirmPassword"].map((field) => (
          <div className="form-group" key={field}>
            <label htmlFor={field}>{field === "password" ? "Password" : "Confirm Password"}:</label>
            <input type="password" id={field} name={field} placeholder={`Enter ${field}`} value={formData[field]} onChange={handleChange} required />
          </div>
        ))}

        {/* Current Company & Job Title */}
        <div className="form-group-inline">
          {["currentCompany", "jobTitle"].map((field) => (
            <div className="form-group" key={field}>
              <label htmlFor={field}>{field.replace(/([A-Z])/g, " $1")}:</label>
              <input type="text" id={field} name={field} placeholder={`Enter ${field}`} value={formData[field]} onChange={handleChange} required />
            </div>
          ))}
        </div>

        {/* Work Experience */}
        <div className="form-group">
          <label htmlFor="workExperience">Work Experience:</label>
          <textarea id="workExperience" name="workExperience" placeholder="Describe your work experience" value={formData.workExperience} onChange={handleChange} required />
        </div>

        


        {/* Higher Education */}
<div className="form-group">
  <label className="form-label">Higher Education:</label>
  <div className="radio-group">
    <label className={`radio-option ${formData.higherEducation === "yes" ? "selected" : ""}`}>
      <input
        type="radio"
        name="higherEducation"
        value="yes"
        checked={formData.higherEducation === "yes"}
        onChange={(e) => setFormData({ ...formData, higherEducation: e.target.value })}
      />
      <span className="custom-radio"></span> Yes
    </label>

    <label className={`radio-option ${formData.higherEducation === "no" ? "selected" : ""}`}>
      <input
        type="radio"
        name="higherEducation"
        value="no"
        checked={formData.higherEducation === "no"}
        onChange={(e) => setFormData({ ...formData, higherEducation: e.target.value })}
      />
      <span className="custom-radio"></span> No
    </label>
  </div>
</div>

{/* Conditional Fields for College & Degree */}
{formData.higherEducation === "yes" && (
  <>
    <div className="form-group">
      <label htmlFor="college">College:</label>
      <input
        type="text"
        id="college"
        name="college"
        placeholder="Enter College Name"
        value={formData.college}
        onChange={handleChange}
      />
    </div>

    <div className="form-group">
      <label htmlFor="degree">Degree:</label>
      <input
        type="text"
        id="degree"
        name="degree"
        placeholder="Enter Degree"
        value={formData.degree}
        onChange={handleChange}
      />
    </div>
  </>
)}

        {/* Submit Button */}
        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
}

export default AlumniDetails;